import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Consultoría";
const h1Text = "Su socio experimentado para una consultoría estratégica en el marco de un desarrollo de negocio integral. Acompañamos a los fabricantes extranjeros de TIC desde el análisis inicial del mercado hasta su implementación exitosa sobre el terreno.";
const heroImage = "https://net-basis.net/Bilder/ict-market-entry-business-strategy-consulting.webp";
const heroAlt = "Un empresario marca Business Strategy para la entrada segura de una empresa TIC en nuevos mercados.";
const heroClaimLine1 = "Seguro en los";
const heroClaimLine2 = "nuevos mercados!";
const heroClaimLine3 = "";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __consultoría__:";
const p1Text = "NetBasis International ofrece una gama completa de servicios de consultoría. Impulsados por un sólido conocimiento y una comprensión profunda del negocio de nuestros socios, desarrollamos estrategias a medida para una expansión exitosa y el rápido cumplimiento de sus objetivos corporativos.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Evaluación fundamentada de los potenciales de negocio específicos en los mercados objetivo",
  "Definición estratégica de la entrada óptima en el mercado",
  "Adaptación precisa a las diferencias ambientales de las nuevas áreas económicas",
  "Asesoramiento experto en la definición de los objetivos comerciales de nuestros socios fabricantes",
  "Creación de planes de ventas y marketing eficaces para el logro seguro de los objetivos",
  "Soporte operativo en la planificación e implementación de todos los pasos necesarios",
  "Consultoría de gestión práctica en cuestiones de integración intercultural",
  "Desarrollo de estrategias de negociación elaboradas y acompañamiento durante su realización",
  "Coordinación eficiente y colaboración con socios comerciales potenciales como fabricantes, distribuidores o revendedores",
];

export default function ConsultoriaEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Servicios de Consultoría | NetBasis International"
        description="Asesoramiento práctico y apoyo estratégico para empresas de TIC que ingresan al mercado de habla alemana."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/consultoria"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-end md:items-end lg:items-end px-6 md:px-10 pointer-events-none mt-6 md:mt-14">
                <div className="flex flex-col md:flex-row lg:flex-row gap-0 md:gap-2 lg:gap-2 animate-[bdTextSlide_12s_ease-in-out_infinite]">
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                    }}
                  >
                    {heroClaimLine1}
                  </p>
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                    }}
                  >
                    {heroClaimLine2}
                  </p>
                  {heroClaimLine3 && (
                    <p
                      className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5]"
                      style={{
                        fontFamily: '"Open Sans", sans-serif',
                      }}
                    >
                      {heroClaimLine3}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}